import { Model } from '@vuex-orm/core'
import { Message } from 'src/store/models/channel/index.js'
import { Channel } from 'src/store/models/index.js'
import userNotificationHandler from '@/config/services/userNotificationHandler.js'
import Channels from 'src/api/channels.js'
import { i18n } from 'src/config/i18n.ts'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export class Chat extends Model {
  static entity = 'chats'
  static fetchUrl = (id, page) => Channels.channelsByIdMessagesUrl(id, page)

  static fields () {
    return {
      id: this.attr(null),
      messages_sent: this.boolean(false),
      created_at: this.attr(null),
      updated_at: this.attr(null)
    }
  }

  static confirmReadingStatus (chatId, channelId) {
    // In case of very fast navigation, chatId/channelId may be '0'/NaN as they are calculated based on router params,
    // which would cause requests to be sent without id and fail with 'HTTP 400 - Bad Request'.
    if (!chatId || !channelId) return

    Channels.postChannelChatByIdRead(chatId, { showMessages: false })
    Channel.resetChannelLastUnreadCount(channelId)
  }

  static async toggleUnReadChat (channel, isUnread = true) {
    if (isUnread) return await Channel.toggleUnReadState(channel, isUnread)

    if (channel.meta.last_unread_count > 0) return this.confirmReadingStatus(channel.chat_id, channel.id)

    return await Channel.toggleUnReadState(channel, isUnread)
  }

  static async updateChannelParticipants (chatId, res) {
    if (!res.meta?.participants) return
    const chatIdNum = Number(chatId)
    const channel = Channel.query().where('chat_id', chatIdNum).first()
    if (!channel) return
    const updatedChannel = {
      ...channel,
      meta: {
        ...(channel.meta || {}),
        participants: res.meta.participants
      }
    }
    await Channel.insertOrUpdate({ data: updatedChannel })
  }

  static async fetchMessagesByDate ({ chatId, lastFetchDate, currentUserId, lastMessageReadDate, channelId }) {
    if (!chatId || !lastFetchDate) return 0

    try {
      const res = await Channels.getChannelChatByIdMessageByDate(chatId, 1, lastFetchDate, { showMessages: false })
      if (!res.data || !res.data.length) return 0

      await Message.insertOrUpdate({ data: res.data })

      await this.updateChannelParticipants(chatId, res)

      this.confirmReadingStatus(chatId, channelId)

      // Only count items which are not from current user and are created after last fetch
      const unreadItems = res.data.filter(message => message.user_id !== currentUserId && (!!lastMessageReadDate && lastMessageReadDate < message.created_at))

      return unreadItems.length
    } catch {
      return 0
    }
  }

  static async fetchMessages (chatId, page, showCachedResults) {
    const url = Channels.channelsByIdMessagesUrl(chatId, page)
    const useCache = page === 1

    const saveData = async res => {
      await Message.insertOrUpdate({ data: res.data })
    }

    // get data either from [cache+network] or only network
    const { data, network } = useCache ? await GlobalPrototypes.cacheHttp(url) : await GlobalPrototypes.axios.get(url)

    if (showCachedResults || !useCache) {
      await saveData({ data })
      await this.updateChannelParticipants(chatId, { data })
    }

    if (!useCache) return { data }

    const res = await network()

    await saveData(res)
    await this.updateChannelParticipants(chatId, res)

    // The cached page shown above can be stale (e.g. the app was closed while new messages
    // arrived server-side), leaving it disconnected from the fresh page with a gap in between
    // that no later "load more" could ever discover on its own. Close that gap in the
    // background - not awaited, so callers (and the loading state they gate on it) aren't
    // blocked on up to MAX_BRIDGE_FETCHES extra round-trips before showing the fresh page.
    if (showCachedResults) this.bridgeCacheGap(chatId, data, res.data).catch(() => {})

    return res
  }

  // Fetches backwards from the fresh page until it reconnects with the (possibly stale) cached
  // page, so the two never leave a permanent hole between them. Bounded to avoid a fetch storm
  // if the cache is very old (e.g. app unused for a long time) - a manual scroll still covers
  // any remainder beyond the cap.
  static async bridgeCacheGap (chatId, cachedMessages, freshMessages) {
    if (!cachedMessages?.length || !freshMessages?.length) return

    const newest = (messages) => messages.reduce((a, b) => (GlobalPrototypes.moment(b.created_at).isAfter(a.created_at) ? b : a))
    const oldest = (messages) => messages.reduce((a, b) => (GlobalPrototypes.moment(b.created_at).isBefore(a.created_at) ? b : a))

    const cachedNewest = newest(cachedMessages)
    let cursor = oldest(freshMessages)
    const MAX_BRIDGE_FETCHES = 5

    for (let i = 0; i < MAX_BRIDGE_FETCHES; i++) {
      if (!GlobalPrototypes.moment(cursor.created_at).isAfter(cachedNewest.created_at)) return // gap closed

      const res = await this.fetchMessagesBefore(chatId, cursor.created_at)
      if (!res.data.length) return // reached the start of the chat's history

      cursor = oldest(res.data)
    }
  }

  static async fetchWithParams (chatId, params) {
    const res = await Channels.getChannelChatMessages(chatId, params)
    await Message.insertOrUpdate({ data: res.data })
    await this.updateChannelParticipants(chatId, res)
    return res
  }

  static async fetchMessageByUuid (chatId, uuid) {
    const res = await Channels.getChannelChatByIdMessageById(chatId, uuid, { showMessages: false })
    await Message.insertOrUpdate({ data: res.data })
    return Message.find(uuid)
  }

  static fetchMessagesBefore (chatId, createdAt) { return this.fetchWithParams(chatId, { before_created_at: createdAt }) }
  static fetchMessagesAfter (chatId, createdAt) { return this.fetchWithParams(chatId, { after_created_at: createdAt }) }

  static async sendMessage (message, id, channelId, abortController) {
    let sendFailed = false
    try {
      const formData = new FormData()
      if (message.file) {
        formData.append('file', message.file.origin)
        formData.append('file_alt_name', message.file.alt_name || '')
      }
      if (message.reply_uuid) { formData.append('reply_uuid', message.reply_uuid) }
      if (message.survey_form_id) { formData.append('survey_form_id', message.survey_form_id) }
      formData.append('content', message.content)

      const store = this.store()

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        showMessages: !!message.file,
        timeout: message.file ? 3 * 60 * 60 * 1000 : import.meta.env.VITE_TIMEOUT,
        signal: abortController?.signal,
        onUploadProgress (progressEvent) {
          const progress = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total))
          message.file && store.commit('chat/SET_FILE_UPLOAD_PROGRESS', {
            id: message.uuid,
            progress: progress,
            file: message.file,
            abortController
          }, { root: true })
        }
      }
      const res = message.survey_form_id ? await Channels.postChannelChatByIdSurvey(id, formData, config) : await Channels.postChannelChatByIdMessages(id, formData, config)

      Message.insertOrUpdate({ data: res.data })
      Channel.updateManually(channelId, res.data)

      userNotificationHandler.playSound('message_sent')
      return res
    } catch (e) {
      sendFailed = !!e?.code
      throw e
    } finally {
      // reset file upload
      if (message.file) {
        await this.store().commit('chat/SET_PENDING_FILE_UPLOAD', { id: message.uuid })

        // deactivated for e2e tests
        // only when upload wasn't canceled by user
        const isE2eTest = window.Cypress || window.Playwright
        if (!isE2eTest) {
          if (!sendFailed) {
            // TODO better suited as a BE response within meta
            // ensure blind users gets feedback about the file upload being done
            GlobalPrototypes.toastNotify({
              type: 'success',
              text: i18n.global.t('chats.message.send_success')
            })
          }

          // fixes a bug, where user gets focus-trap on (removed) progress-bar, after sending a file
          const chatDropdown = document.querySelector('.chat-dropdown')
          chatDropdown?.focus()
        }
      }
    }
  }

  static async sendKnock (chatId, channelId, subject = '') {
    const res = await Channels.postChannelChatByIdKnock(chatId, { content: subject })
    Message.insertOrUpdate({ data: res.data })
    // after sending knock update the channels meta data
    Channel.updateManually(channelId, res.data)
    userNotificationHandler.playSound('message_sent')
    return res
  }

  static async sendVoice (chatId, channelId, { file: { blob: audio } }, duration) {
    const formData = new FormData()
    formData.append('file', audio)
    formData.append('duration_in_seconds', duration)

    const res = await Channels.postChannelChatByIdVoice(chatId, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    Message.insertOrUpdate({ data: res.data })
    Channel.updateManually(channelId, res.data)
    userNotificationHandler.playSound('message_sent')
    return res
  }

  static async updateNotificationCounter (newNotificationCount) {
    await this.store().dispatch('notification/updateUnreadNotificationCount', newNotificationCount || 0)
  }
}

export default Chat
